/**
 * PostHog Event Tracking via Axios Interceptor
 *
 * Captures user actions by intercepting successful axios responses
 * and mapping URL patterns to PostHog events with Title Case naming.
 */

const EVENT_MAP = [
    // === Registration Funnel ===
    { method: 'post', pattern: /^\/apply$/, event: 'Applied', props: (d) => pick(d, ['email', 'first_name', 'last_name']) },
    { method: 'post', pattern: /^\/application\/validate$/, event: 'Application Validated' },
    { method: 'post', pattern: /^\/registration\/[^/]+$/, event: 'Registration Submitted' },
    { method: 'post', pattern: /^\/registration\/[^/]+\/validate$/, event: 'Registration Validated' },
    { method: 'post', pattern: /^\/jointheclub$/, event: 'Direct Registration Submitted' },
    { method: 'post', pattern: /^\/jointheclub\/validate$/, event: 'Direct Registration Validated' },
    { method: 'post', pattern: /^\/invite-registration$/, event: 'Invite Registration Submitted' },
    { method: 'post', pattern: /^\/invite-registration\/validate$/, event: 'Invite Registration Validated' },
    { method: 'put', pattern: /^\/vip-registration$/, event: 'VIP Registration Submitted' },

    // === Abandoned Funnel ===
    { method: 'post', pattern: /^\/abandoned-funnel\/application$/, event: 'Application Abandoned' },
    { method: 'patch', pattern: /^\/abandoned-funnel\/application$/, event: 'Application Abandoned Updated' },
    { method: 'post', pattern: /^\/abandoned-funnel\/registration$/, event: 'Registration Abandoned' },
    { method: 'post', pattern: /^\/abandoned-funnel\/referrals$/, event: 'Referral Registration Abandoned' },

    // === Reviews ===
    { method: 'post', pattern: /^\/spot\/[^/]+\/reviews$/, event: 'Leave a Review', props: (d, url) => ({ spot: extractSlug(url, 'spot') }) },
    { method: 'patch', pattern: /^\/review\/[^/]+\/love$/, event: 'Love a Review' },
    { method: 'post', pattern: /^\/review-love$/, event: 'Love a Review' },
    { method: 'post', pattern: /^\/review\/[^/]+\/response$/, event: 'Leave a Comment' },

    // === Lists ===
    { method: 'post', pattern: /^\/lists\/store$/, event: 'Create a List', props: (d) => ({ list_name: d?.list?.title }) },
    { method: 'post', pattern: /^\/lists\/add-to-list$/, event: 'Spot Added to a List' },
    { method: 'post', pattern: /^\/lists\/follow$/, event: 'Follow List' },
    { method: 'post', pattern: /^\/lists\/delete$/, event: 'Delete a List' },
    { method: 'delete', pattern: /^\/lists\/[^/]+\/spot\/[^/]+$/, event: 'Spot Removed from List' },
    { method: 'patch', pattern: /^\/lists\/[^/]+\/spot\/[^/]+\/sort$/, event: 'Spot Reordered in List' },
    { method: 'patch', pattern: /^\/lists\/[^/]+\/spot\/[^/]+\/update-caption$/, event: 'Spot Caption Updated' },
    { method: 'post', pattern: /^\/lists\/cover$/, event: 'List Cover Updated' },

    // === List Schedules ===
    { method: 'patch', pattern: /^\/lists\/[^/]+\/schedules\/[^/]+$/, event: 'Trip Date Scheduled' },
    { method: 'delete', pattern: /^\/lists\/[^/]+\/schedules\/[^/]+$/, event: 'Trip Date Removed' },

    // === Duplicate List ===
    { method: 'post', pattern: /^\/lists\/duplicate$/, event: 'Duplicate a List' },

    // === Follow/Unfollow Members ===
    { method: 'patch', pattern: /^\/user\/[^/]+\/follow$/, event: 'Toggle Follow Member', props: (d, url) => ({ member: extractSlug(url, 'user') }) },

    // === Booking ===
    { method: 'post', pattern: /^\/book$/, event: 'Submit Booking Request' },
    { method: 'post', pattern: /^\/payment-intent/, event: 'Payment Intent Created' },

    // === Messages ===
    { method: 'post', pattern: /^\/messages$/, event: 'Message a Member' },
    { method: 'post', pattern: /^\/conversations$/, event: 'Conversation Started' },

    // === Referrals ===
    { method: 'post', pattern: /^\/referrals\/invite$/, event: 'Invite a Friend', props: (d) => ({ method: 'email' }) },
    { method: 'post', pattern: /^\/referrals\/invite-phone$/, event: 'Invite a Friend', props: (d) => ({ method: 'phone' }) },
    { method: 'post', pattern: /^\/referrals\/remind$/, event: 'Referral Reminder Sent' },

    // === Suggest Spot ===
    { method: 'get', pattern: /^\/suggest-spot-check\//, event: 'Suggest Spot Search' },

    // === Notifications ===
    { method: 'post', pattern: /^\/notification\/read\//, event: 'Notification Read' },

    // === Account ===
    { method: 'post', pattern: /^\/account\/[^/]+\/winback$/, event: 'Winback Accepted' },
    { method: 'post', pattern: /^\/account\/[^/]+\/cover$/, event: 'Profile Cover Updated' },

    // === Destinations: Spot Browsing ===
    { method: 'post', pattern: /^\/destinations\/[^/]+\/spots$/, event: 'Destination Spots Browsed', props: (d, url) => ({ city: extractSlug(url, 'destinations') }) },

    // === Partner Bookings (uses fetch, tracked separately) ===
    // Handled via patchFetch below
];

/**
 * Extract a slug from a URL path segment.
 * e.g., extractSlug('/spot/ten-thousand-waves/reviews', 'spot') => 'ten-thousand-waves'
 */
function extractSlug(url, segment) {
    const parts = url.split('/');
    const idx = parts.indexOf(segment);
    return idx !== -1 && parts[idx + 1] ? parts[idx + 1] : undefined;
}

/**
 * Pick specific keys from an object, ignoring undefined values.
 */
function pick(obj, keys) {
    if (!obj || typeof obj !== 'object') return {};
    const result = {};
    keys.forEach(k => { if (obj[k] !== undefined) result[k] = obj[k]; });
    return result;
}

/**
 * Safely capture a PostHog event.
 */
function capture(event, properties) {
    if (typeof window.posthog !== 'undefined' && window.posthog.capture) {
        window.posthog.capture(event, properties || {});
    }
}

/**
 * Setup axios response interceptor for PostHog event tracking.
 */
function setupAxiosInterceptor() {
    if (!window.axios) return;

    window.axios.interceptors.response.use(function (response) {
        try {
            var config = response.config || {};
            var method = (config.method || '').toLowerCase();
            var url = (config.url || '').split('?')[0]; // strip query params

            for (var i = 0; i < EVENT_MAP.length; i++) {
                var mapping = EVENT_MAP[i];
                if (mapping.method === method && mapping.pattern.test(url)) {
                    var properties = {};
                    if (typeof mapping.props === 'function') {
                        var requestData = config.data;
                        if (typeof requestData === 'string') {
                            try { requestData = JSON.parse(requestData); } catch (e) { requestData = {}; }
                        }
                        properties = mapping.props(requestData, url) || {};
                    }
                    capture(mapping.event, properties);
                    break;
                }
            }
        } catch (e) {
            // Silently fail — analytics should never break the app
        }
        return response;
    });
}

/**
 * Patch window.fetch to capture partner booking events.
 */
function patchFetch() {
    if (!window.fetch) return;

    var originalFetch = window.fetch;
    window.fetch = function () {
        var url = arguments[0];
        var options = arguments[1] || {};

        return originalFetch.apply(this, arguments).then(function (response) {
            try {
                if (response.ok) {
                    var method = (options.method || 'GET').toUpperCase();
                    var pathname = typeof url === 'string' ? url.split('?')[0] : '';

                    if (method === 'POST' && /^\/partner\/[^/]+\/book$/.test(pathname)) {
                        capture('Submit Partner Booking Request', {
                            partner: extractSlug(pathname, 'partner')
                        });
                    }
                }
            } catch (e) {
                // Silently fail
            }
            return response;
        });
    };
}

/**
 * Track funnel page views based on current URL path.
 */
function trackFunnelPageViews() {
    var path = window.location.pathname;

    var funnelPages = [
        { pattern: /^\/$/, event: 'View Gate Page' },
        { pattern: /^\/membership$/, event: 'View Membership Page' },
        { pattern: /^\/apply$/, event: 'View Application Form' },
        { pattern: /^\/application\/complete$/, event: 'View Application Complete' },
        { pattern: /^\/registration$/, event: 'View Registration Form' },
        { pattern: /^\/registration\/complete$/, event: 'View Registration Complete' },
        { pattern: /^\/jointheclub$/, event: 'View Direct Registration Form' },
        { pattern: /^\/invite-registration\//, event: 'View Invite Registration Form' },
        { pattern: /^\/vip-registration$/, event: 'View VIP Registration Form' },
    ];

    for (var i = 0; i < funnelPages.length; i++) {
        if (funnelPages[i].pattern.test(path)) {
            capture(funnelPages[i].event);
            break;
        }
    }
}

// Initialize
setupAxiosInterceptor();
patchFetch();

// Track funnel views after DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackFunnelPageViews);
} else {
    trackFunnelPageViews();
}
